"use client";
import * as React from "react";
import { FullPageForm, type FormField, type LineItemColumn } from "./FullPageForm";
import { useERP, type ID, type ERPState } from "@/lib/erp/store";
import { useToast } from "@/hooks/use-toast";

export type FormPageType = "product" | "customer" | "supplier" | "warehouse" | "po" | "so" | "rma";

export interface FullPageFormPageProps {
  formType: FormPageType;
  onBack: () => void;
  onSaved?: (id: ID) => void;
  editId?: ID;
}

export function FullPageFormPage({ formType, onBack, onSaved, editId }: FullPageFormPageProps) {
  const erp = useERP();
  const { toast } = useToast();

  const config = React.useMemo(() => buildConfig(formType, erp, editId), [formType, erp, editId]);

  const handleSubmit = (values: Record<string, unknown>, lineItems: Record<string, unknown>[]) => {
    try {
      let id: ID = "";
      if (formType === "product") {
        if (editId) { erp.updateProduct(editId, values as never); id = editId; }
        else id = erp.addProduct(values as never);
      } else if (formType === "customer") {
        if (editId) { erp.updateCustomer(editId, values as never); id = editId; }
        else id = erp.addCustomer(values as never);
      } else if (formType === "supplier") {
        if (editId) { erp.updateSupplier(editId, values as never); id = editId; }
        else id = erp.addSupplier(values as never);
      } else if (formType === "warehouse") {
        // M12 fix: support warehouse edit mode
        if (editId) { erp.updateWarehouse(editId, values as never); id = editId; }
        else id = erp.addWarehouse(values as never);
      } else if (formType === "po") {
        id = erp.createPO({
          supplierId: String(values.supplierId),
          warehouseId: String(values.warehouseId),
          orderDate: String(values.orderDate),
          expectedDelivery: String(values.expectedDelivery),
          lines: lineItems.map(li => ({ productId: String(li.productId), qty: Number(li.qty), unitPrice: Number(li.unitPrice), receivedQty: 0 })),
          notes: String(values.notes ?? ""),
        } as never);
      } else if (formType === "so") {
        id = erp.createSO({
          customerId: String(values.customerId),
          warehouseId: String(values.warehouseId),
          orderDate: String(values.orderDate),
          requestedDelivery: String(values.requestedDelivery),
          lines: lineItems.map(li => ({ productId: String(li.productId), qty: Number(li.qty), unitPrice: Number(li.unitPrice), discount: Number(li.discount ?? 0), taxRate: 15 })),
          notes: String(values.notes ?? ""),
        } as never);
      } else if (formType === "rma") {
        id = erp.createReturn({
          originalInvoiceId: String(values.originalInvoiceId),
          customerId: String(values.customerId),
          returnDate: String(values.returnDate),
          reason: String(values.reason),
          lines: lineItems.map(li => ({ productId: String(li.productId), qty: Number(li.qty), condition: String(li.condition) as never })),
          creditAmount: Number(values.creditAmount ?? 0),
        } as never);
      }
      toast({ title: "Saved", description: `${config.title} saved successfully.` });
      onSaved?.(id);
      onBack();
    } catch (e) {
      toast({ title: "Error", description: String(e), variant: "destructive" });
    }
  };

  return (
    <FullPageForm
      title={config.title}
      description={config.description}
      fields={config.fields}
      lineItemColumns={config.lineItemColumns}
      lineItemDefault={config.lineItemDefault}
      defaultLineItemsCount={config.defaultLineItemsCount}
      onBack={onBack}
      onSubmit={handleSubmit}
      submitLabel={editId ? "Update" : "Create"}
      // M11 fix: SO uses salePrice, PO/RMA use costPrice
      productPriceField={formType === "so" ? "salePrice" : "costPrice"}
    />
  );
}

function buildConfig(formType: FormPageType, erp: ERPState, editId?: ID): {
  title: string;
  description: string;
  fields: FormField[];
  lineItemColumns?: LineItemColumn[];
  lineItemDefault?: Record<string, unknown>;
  defaultLineItemsCount?: number;
} {
  if (formType === "product") {
    const p = editId ? erp.products.find(x => x.id === editId) : undefined;
    return {
      title: editId ? "Edit Product" : "New Product",
      description: "Add a new SKU to the product master.",
      fields: [
        { key: "sku", label: "SKU", type: "text", required: true, defaultValue: p?.sku ?? "" },
        { key: "name", label: "Product Name", type: "text", required: true, colSpan: 2, defaultValue: p?.name ?? "" },
        { key: "barcode", label: "Barcode", type: "text", defaultValue: p?.barcode ?? "" },
        { key: "category", label: "Category", type: "text", defaultValue: p?.category ?? "" },
        { key: "brand", label: "Brand", type: "text", defaultValue: p?.brand ?? "" },
        { key: "uom", label: "UOM", type: "select", options: [{ value: "Piece", label: "Piece" }, { value: "Bag", label: "Bag" }, { value: "Box", label: "Box" }, { value: "Kg", label: "Kg" }, { value: "Litre", label: "Litre" }], defaultValue: p?.uom ?? "Piece" },
        { key: "costPrice", label: "Cost Price (৳)", type: "money", required: true, defaultValue: p?.costPrice ?? 0 },
        { key: "salePrice", label: "Sale Price (৳)", type: "money", required: true, defaultValue: p?.salePrice ?? 0 },
        { key: "mrp", label: "MRP (৳)", type: "money", defaultValue: p?.mrp ?? 0 },
        { key: "reorderPoint", label: "Reorder Point", type: "number", defaultValue: p?.reorderPoint ?? 0 },
        { key: "status", label: "Status", type: "select", options: [{ value: "Active", label: "Active" }, { value: "Discontinued", label: "Discontinued" }], defaultValue: p?.status ?? "Active" },
      ],
    };
  }
  if (formType === "customer") {
    const c = editId ? erp.customers.find(x => x.id === editId) : undefined;
    return {
      title: editId ? "Edit Customer" : "New Customer",
      description: "Register a customer or distributor in the master.",
      fields: [
        { key: "code", label: "Customer Code", type: "text", required: true, defaultValue: c?.code ?? "" },
        { key: "name", label: "Customer Name", type: "text", required: true, colSpan: 2, defaultValue: c?.name ?? "" },
        { key: "type", label: "Type", type: "select", options: [{ value: "Distributor", label: "Distributor" }, { value: "Retailer", label: "Retailer" }, { value: "Corporate", label: "Corporate" }, { value: "Institutional", label: "Institutional" }], defaultValue: c?.type ?? "Distributor" },
        { key: "zone", label: "Zone", type: "text", defaultValue: c?.zone ?? "" },
        { key: "phone", label: "Phone", type: "text", defaultValue: c?.phone ?? "" },
        { key: "email", label: "Email", type: "text", colSpan: 2, defaultValue: c?.email ?? "" },
        { key: "creditLimit", label: "Credit Limit (৳)", type: "money", defaultValue: c?.creditLimit ?? 0 },
        { key: "creditDays", label: "Credit Days", type: "number", defaultValue: c?.creditDays ?? 0 },
        { key: "outstanding", label: "Opening Outstanding (৳)", type: "money", defaultValue: c?.outstanding ?? 0 },
        { key: "status", label: "Status", type: "select", options: [{ value: "Active", label: "Active" }, { value: "Blocked", label: "Blocked" }, { value: "Watch", label: "Watch" }], defaultValue: c?.status ?? "Active" },
      ],
    };
  }
  if (formType === "supplier") {
    const s = editId ? erp.suppliers.find(x => x.id === editId) : undefined;
    return {
      title: editId ? "Edit Supplier" : "New Supplier",
      description: "Add a vendor to the supplier master.",
      fields: [
        { key: "code", label: "Supplier Code", type: "text", required: true, defaultValue: s?.code ?? "" },
        { key: "name", label: "Supplier Name", type: "text", required: true, colSpan: 2, defaultValue: s?.name ?? "" },
        { key: "category", label: "Category", type: "text", defaultValue: s?.category ?? "" },
        { key: "contactPerson", label: "Contact Person", type: "text", defaultValue: s?.contactPerson ?? "" },
        { key: "phone", label: "Phone", type: "text", defaultValue: s?.phone ?? "" },
        { key: "email", label: "Email", type: "text", colSpan: 2, defaultValue: s?.email ?? "" },
        { key: "leadTimeDays", label: "Lead Time (days)", type: "number", defaultValue: s?.leadTimeDays ?? 7 },
        { key: "rating", label: "Rating (0-5)", type: "number", defaultValue: s?.rating ?? 4 },
        { key: "paymentTerms", label: "Payment Terms", type: "text", defaultValue: s?.paymentTerms ?? "Net 30" },
        { key: "status", label: "Status", type: "select", options: [{ value: "Active", label: "Active" }, { value: "Inactive", label: "Inactive" }], defaultValue: s?.status ?? "Active" },
      ],
    };
  }
  if (formType === "warehouse") {
    // M12 fix: pre-fill fields for edit mode
    const w = editId ? erp.warehouses.find(x => x.id === editId) : undefined;
    return {
      title: editId ? "Edit Warehouse" : "New Warehouse",
      description: "Define a new warehouse or depot.",
      fields: [
        { key: "code", label: "Warehouse Code", type: "text", required: true, defaultValue: w?.code ?? "" },
        { key: "name", label: "Warehouse Name", type: "text", required: true, colSpan: 2, defaultValue: w?.name ?? "" },
        { key: "type", label: "Type", type: "select", options: [{ value: "Main", label: "Main" }, { value: "Regional", label: "Regional" }, { value: "Depot", label: "Depot" }, { value: "Hub", label: "Hub" }], defaultValue: w?.type ?? "Main" },
        { key: "branch", label: "Branch", type: "text", defaultValue: w?.branch ?? "" },
        { key: "capacity", label: "Capacity (units)", type: "number", defaultValue: w?.capacity ?? 0 },
        { key: "utilization", label: "Utilization (%)", type: "number", defaultValue: w?.utilization ?? 0 },
        { key: "status", label: "Status", type: "select", options: [{ value: "Active", label: "Active" }, { value: "Maintenance", label: "Maintenance" }], defaultValue: w?.status ?? "Active" },
      ],
    };
  }
  if (formType === "po") {
    return {
      title: "Create Purchase Order",
      description: "Raise a new PO to a supplier. Will be queued for approval.",
      fields: [
        { key: "supplierId", label: "Supplier", type: "supplier-picker", required: true, colSpan: 2 },
        { key: "warehouseId", label: "Deliver To Warehouse", type: "warehouse-picker", required: true, colSpan: 2 },
        { key: "orderDate", label: "Order Date", type: "date", required: true, defaultValue: new Date().toISOString().slice(0, 10) },
        { key: "expectedDelivery", label: "Expected Delivery", type: "date", required: true },
        { key: "notes", label: "Notes / Terms", type: "textarea", colSpan: 4 },
      ],
      lineItemColumns: [
        { key: "productId", label: "Product", type: "product-picker" },
        { key: "qty", label: "Qty", type: "number", width: "100px" },
        { key: "unitPrice", label: "Unit Price", type: "money", width: "140px" },
      ],
      lineItemDefault: { productId: "", qty: 1, unitPrice: 0 },
      defaultLineItemsCount: 1,
    };
  }
  if (formType === "so") {
    return {
      title: "Create Sales Order",
      description: "Raise a new SO. Credit check will run automatically.",
      fields: [
        { key: "customerId", label: "Customer", type: "customer-picker", required: true, colSpan: 2 },
        { key: "warehouseId", label: "Ship From Warehouse", type: "warehouse-picker", required: true, colSpan: 2 },
        { key: "orderDate", label: "Order Date", type: "date", required: true, defaultValue: new Date().toISOString().slice(0, 10) },
        { key: "requestedDelivery", label: "Requested Delivery", type: "date", required: true },
        { key: "notes", label: "Notes", type: "textarea", colSpan: 4 },
      ],
      lineItemColumns: [
        { key: "productId", label: "Product", type: "product-picker" },
        { key: "qty", label: "Qty", type: "number", width: "90px" },
        { key: "unitPrice", label: "Unit Price", type: "money", width: "130px" },
        { key: "discount", label: "Disc %", type: "number", width: "90px" },
      ],
      lineItemDefault: { productId: "", qty: 1, unitPrice: 0, discount: 0 },
      defaultLineItemsCount: 1,
    };
  }
  if (formType === "rma") {
    return {
      title: "Create RMA / Sales Return",
      description: "Initiate a return request against an invoice.",
      fields: [
        { key: "originalInvoiceId", label: "Original Invoice", type: "select", required: true, colSpan: 2, options: erp.salesInvoices.map(i => ({ value: i.id, label: i.invoiceNumber })) },
        { key: "customerId", label: "Customer", type: "customer-picker", required: true, colSpan: 2 },
        { key: "returnDate", label: "Return Date", type: "date", required: true, defaultValue: new Date().toISOString().slice(0, 10) },
        { key: "creditAmount", label: "Credit Amount (৳)", type: "money", required: true },
        { key: "reason", label: "Reason for Return", type: "textarea", colSpan: 4 },
      ],
      lineItemColumns: [
        { key: "productId", label: "Product", type: "product-picker" },
        { key: "qty", label: "Qty", type: "number", width: "100px" },
        { key: "condition", label: "Condition", type: "select", width: "160px", options: [
          { value: "Resalable", label: "Resalable" }, { value: "Damaged", label: "Damaged" },
          { value: "Repairable", label: "Repairable" }, { value: "Scrap", label: "Scrap" },
        ] },
      ],
      lineItemDefault: { productId: "", qty: 1, condition: "Resalable" },
      defaultLineItemsCount: 1,
    };
  }
  return { title: "Form", description: "", fields: [] };
}
